import { DeleteConversationsDialog } from "@app/components/assistant/conversation/DeleteConversationsDialog";
import { EditConversationTitleDialog } from "@app/components/assistant/conversation/EditConversationTitleDialog";
import { LeaveConversationDialog } from "@app/components/assistant/conversation/LeaveConversationDialog";
import { ConfirmContext } from "@app/components/Confirm";
import { InviteToConversationDialog } from "@app/components/people/InviteToConversationDialog";
import {
  useBranchConversation,
  useConversation,
  useConversationParticipants,
  useConversationParticipationOptions,
  useConversationUrlAccessMode,
  useJoinConversation,
} from "@app/hooks/conversations";
import { useDeleteConversation } from "@app/hooks/useDeleteConversation";
import { useSendNotification } from "@app/hooks/useNotification";
import { useURLSheet } from "@app/hooks/useURLSheet";
import config from "@app/lib/api/config";
import { useAuth } from "@app/lib/auth/AuthContext";
import { useClientType } from "@app/lib/context/clientType";
import { useAppRouter } from "@app/lib/platform";
import { useWorkspacePermissions } from "@app/lib/swr/permissions";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { hasHealthyProviders } from "@app/lib/utils/providersHealth";
import {
  getAgentBuilderRoute,
  getConversationRoute,
  getPodRoute,
  setQueryParam,
} from "@app/lib/utils/router";
import { agentAvatarVisual } from "@app/types/assistant/avatar";
import {
  type ConversationListItemType,
  getConversationDisplayTitle,
  getConversationUrlAccessMode,
  isPodConversation,
} from "@app/types/assistant/conversation";
import type { WorkspaceType } from "@app/types/user";
import { isPrivateConversationUrlsByDefaultEnabled } from "@app/types/user";
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Edit04,
  Eye,
  EyeOff,
  GitBranch01,
  Link01,
  LinkExternal01,
  PlusCircle,
  Sidekick,
  Trash01,
  UserPlus01,
  UserSquare,
  XClose,
} from "@dust-tt/sparkle";
import type React from "react";
import type { ReactElement } from "react";
import { useCallback, useContext, useEffect, useState } from "react";

/**
 * Hook for handling right-click context menu with timing protection
 *
 * This hook solves the "double right-click" problem where right-clicking while
 * a menu is open would cause it to close and immediately reopen at the cursor position.
 *
 * The core issue: DropdownMenu doesn't add a backdrop to catch events, so right-clicks
 * while the menu is open still trigger our handlers. Due to React's async state updates,
 * when the menu closes, our right-click handler sees isMenuOpen as false and reopens the menu.
 */
export function useConversationMenu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuTriggerPosition, setMenuTriggerPosition] = useState<
    { x: number; y: number } | undefined
  >();

  // Tracks if the menu was just closed to prevent immediate reopening
  // This flag creates a brief "cooldown" period after menu closure
  const [wasMenuJustClosed, setWasMenuJustClosed] = useState(false);

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      // When menu closes, set the "just closed" flag for 100ms
      // This prevents right-click handlers from immediately reopening the menu
      setWasMenuJustClosed(true);
      setTimeout(() => {
        setWasMenuJustClosed(false);
      }, 100);
    }
  }, []);

  const handleRightClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ignore right-clicks if menu is currently open OR was just closed
      // This prevents the close -> immediate reopen behavior
      if (isMenuOpen || wasMenuJustClosed) {
        return;
      }

      // Open menu at cursor position
      setMenuTriggerPosition({ x: e.clientX, y: e.clientY });
      setIsMenuOpen(true);
    },
    [isMenuOpen, wasMenuJustClosed]
  );

  // Clear the trigger position when menu closes to allow animations to complete
  // The 150ms delay ensures smooth closing animation before position reset
  useEffect(() => {
    if (!isMenuOpen) {
      setTimeout(() => {
        setMenuTriggerPosition(undefined);
      }, 150);
    }
  }, [isMenuOpen]);

  return {
    isMenuOpen,
    menuTriggerPosition,
    handleRightClick,
    handleMenuOpenChange,
  };
}

interface ConversationMenuProps {
  activeConversationId: string | null;
  conversation?: ConversationListItemType;
  owner: WorkspaceType;
  trigger:
    | ReactElement
    | (({ isPendingAction }: { isPendingAction: boolean }) => ReactElement);
  isConversationDisplayed: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  triggerPosition?: { x: number; y: number };
  displayOpenInBrowser?: boolean;
  openDetailsInNewTab?: boolean;
  onConversationDeleted?: () => void;
}

export function ConversationMenu({
  activeConversationId,
  conversation,
  owner,
  trigger,
  isConversationDisplayed,
  isOpen,
  onOpenChange,
  triggerPosition,
  displayOpenInBrowser,
  openDetailsInNewTab,
  onConversationDeleted,
}: ConversationMenuProps) {
  const { user, providersHealth } = useAuth();
  const confirm = useContext(ConfirmContext);

  const clientType = useClientType();
  const isMobile = useIsMobile();

  const router = useAppRouter();

  const { hasPermission } = useWorkspacePermissions();

  const canCreateAgent = hasPermission("create", "agent");
  const canTurnIntoAgent =
    !!conversation &&
    !!user &&
    canCreateAgent &&
    !isMobile &&
    clientType !== "extension";
  const sendNotification = useSendNotification();

  const { onOpenChange: onOpenChangeAgentModal } = useURLSheet("agentDetails");
  const { onOpenChange: onOpenChangeUserModal } = useURLSheet("userDetails");

  const handleSeeAgentDetails = (agentId: string) => {
    if (openDetailsInNewTab) {
      const agentDetailsUrl = getConversationRoute(
        owner.sId,
        activeConversationId,
        `agentDetails=${agentId}`,
        config.getAppUrl()
      );
      window.open(agentDetailsUrl, "_blank");
      return;
    }
    onOpenChangeAgentModal(true);
    setQueryParam(router, "agentDetails", agentId);
  };

  const handleSeeUserDetails = (userId: string) => {
    if (openDetailsInNewTab) {
      const userDetailsUrl = getConversationRoute(
        owner.sId,
        activeConversationId,
        `userDetails=${userId}`,
        config.getAppUrl()
      );
      window.open(userDetailsUrl, "_blank");
      return;
    }
    onOpenChangeUserModal(true);
    setQueryParam(router, "userDetails", userId);
  };

  const shouldWaitBeforeFetching =
    activeConversationId === null || user?.sId === undefined || !isOpen;
  const { mutateConversation } = useConversation({
    conversationId: isConversationDisplayed ? activeConversationId : null,
    workspaceId: owner.sId,
    options: { disabled: !isConversationDisplayed },
  });
  const conversationParticipationOptions = useConversationParticipationOptions({
    ownerId: owner.sId,
    conversationId: activeConversationId,
    userId: user?.sId ?? null,
    disabled: shouldWaitBeforeFetching,
  });
  const { conversationParticipants } = useConversationParticipants({
    conversationId: activeConversationId,
    workspaceId: owner.sId,
    options: {
      disabled: shouldWaitBeforeFetching,
    },
  });

  const joinConversation = useJoinConversation({
    ownerId: owner.sId,
    conversationId: activeConversationId,
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
  const [showInviteDialog, setShowInviteDialog] = useState<boolean>(false);
  const handleConversationBranched = useCallback(() => {
    if (isConversationDisplayed) {
      void mutateConversation();
    }
  }, [isConversationDisplayed, mutateConversation]);
  const { branchConversation, isBranching } = useBranchConversation({
    owner,
    conversationId: activeConversationId,
    onConversationBranched: handleConversationBranched,
  });
  const menuTrigger =
    typeof trigger === "function"
      ? trigger({ isPendingAction: isBranching })
      : trigger;

  const conversationLink = getConversationRoute(
    owner.sId,
    activeConversationId,
    undefined,
    config.getAppUrl()
  );

  const doDelete = useDeleteConversation(owner);
  const leaveOrDelete = useCallback(
    async (forceDelete: boolean = false) => {
      const res = await doDelete(conversation, forceDelete);
      if (res) {
        onConversationDeleted?.();
      }
      if (isConversationDisplayed && res) {
        const redirectRoute =
          conversation && isPodConversation(conversation)
            ? getPodRoute(owner.sId, conversation.spaceId)
            : getConversationRoute(owner.sId);
        void router.push(redirectRoute);
      }
    },
    [
      conversation,
      doDelete,
      owner.sId,
      router,
      isConversationDisplayed,
      onConversationDeleted,
    ]
  );

  const copyConversationLink = useCallback(async () => {
    await navigator.clipboard.writeText(conversationLink ?? "");
    sendNotification({ type: "success", title: "Link copied !" });
  }, [conversationLink, sendNotification]);

  const openConversationInBrowser = () => {
    window.open(conversationLink, "_blank");
  };
  const {
    isUpdatingConversationUrlAccessMode,
    updateConversationUrlAccessMode,
  } = useConversationUrlAccessMode({
    owner,
    conversationId: activeConversationId,
  });

  if (!activeConversationId) {
    return null;
  }

  const canJoin = conversationParticipationOptions.includes("join");
  const canLeave = conversationParticipationOptions.includes("leave");
  const canDelete = conversationParticipationOptions.includes("delete");
  const isPrivateByDefault = isPrivateConversationUrlsByDefaultEnabled(owner);
  const isPodConversationWithOwnUrl =
    conversation !== undefined && isPodConversation(conversation);
  const conversationUrlAccessMode = getConversationUrlAccessMode(
    conversation?.metadata
  );
  const canMakeUrlAccessible =
    isPrivateByDefault &&
    !isPodConversationWithOwnUrl &&
    conversationUrlAccessMode !== "workspace_members";
  const canRestrictUrlAccess =
    isPrivateByDefault &&
    !isPodConversationWithOwnUrl &&
    conversationUrlAccessMode === "workspace_members";

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      <DeleteConversationsDialog
        isOpen={showDeleteDialog}
        type="selection"
        selectedCount={1}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={() => {
          setShowDeleteDialog(false);
          void leaveOrDelete(true);
        }}
      />
      <LeaveConversationDialog
        isOpen={showLeaveDialog}
        onClose={() => setShowLeaveDialog(false)}
        onLeave={() => {
          setShowLeaveDialog(false);
          void leaveOrDelete();
        }}
      />
      <InviteToConversationDialog
        owner={owner}
        conversationId={activeConversationId}
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
      />
      <EditConversationTitleDialog
        isOpen={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        owner={owner}
        conversationId={activeConversationId}
        currentTitle={
          conversation ? getConversationDisplayTitle(conversation) : ""
        }
      />
      <DropdownMenu modal={false} open={isOpen} onOpenChange={onOpenChange}>
        {triggerPosition ? (
          <>
            {menuTrigger}
            <DropdownMenuTrigger asChild>
              <div
                style={{
                  position: "fixed",
                  left: triggerPosition.x,
                  top: triggerPosition.y,
                  width: 0,
                  height: 0,
                  pointerEvents: "none",
                }}
              />
            </DropdownMenuTrigger>
          </>
        ) : (
          <DropdownMenuTrigger asChild>{menuTrigger}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent onFocusOutside={(e) => e.preventDefault()}>
          <DropdownMenuItem
            label="Rename conversation"
            onClick={() => setShowRenameDialog(true)}
            icon={Edit04}
          />
          <DropdownMenuItem
            label="Branch conversation"
            onClick={() => {
              void branchConversation();
            }}
            icon={GitBranch01}
            disabled={isBranching}
          />
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              icon={UserSquare}
              label="Participants"
              disabled={
                !conversationParticipants?.users.length &&
                !conversationParticipants?.agents.length &&
                !canJoin
              }
            />
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {canJoin && (
                  <>
                    <DropdownMenuItem
                      label="Join"
                      onClick={joinConversation}
                      icon={PlusCircle}
                    />
                    <DropdownMenuSeparator />
                  </>
                )}
                {conversationParticipants?.agents.map((agent) => (
                  <DropdownMenuItem
                    key={agent.configurationId}
                    label={agent.name}
                    onClick={() => handleSeeAgentDetails(agent.configurationId)}
                    icon={
                      <Avatar
                        size="xs"
                        visual={agentAvatarVisual({
                          sId: agent.configurationId,
                          name: agent.name,
                          pictureUrl: agent.pictureUrl,
                        })}
                        name={agent.name}
                      />
                    }
                  />
                ))}
                {conversationParticipants?.users.map((user) => (
                  <DropdownMenuItem
                    key={user.sId}
                    label={user.fullName ?? user.username}
                    onClick={() => handleSeeUserDetails(user.sId)}
                    icon={
                      <Avatar
                        size="xs"
                        visual={user.pictureUrl}
                        name={user.fullName ?? user.username}
                        isRounded
                      />
                    }
                    // biome-ignore lint/plugin/noCssImportant: legacy [GEN12] — needs cleanup
                    className="text-foreground!"
                  />
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          {displayOpenInBrowser && conversationLink && (
            <DropdownMenuItem
              label="Open in a browser tab"
              onClick={openConversationInBrowser}
              icon={LinkExternal01}
            />
          )}
          {conversationLink && (
            <DropdownMenuItem
              label="Copy link"
              onClick={copyConversationLink}
              icon={Link01}
            />
          )}
          <DropdownMenuItem
            label="Invite people"
            onClick={() => setShowInviteDialog(true)}
            icon={UserPlus01}
          />
          {(canMakeUrlAccessible || canRestrictUrlAccess) && (
            <DropdownMenuItem
              label={
                canRestrictUrlAccess
                  ? "Restrict URL access"
                  : "Make URL accessible"
              }
              onClick={() => {
                void updateConversationUrlAccessMode(
                  canRestrictUrlAccess
                    ? "participants_only"
                    : "workspace_members"
                );
              }}
              icon={canRestrictUrlAccess ? EyeOff : Eye}
              disabled={isUpdatingConversationUrlAccessMode}
            />
          )}
          {canTurnIntoAgent && (
            <DropdownMenuItem
              label="Convert to agent"
              icon={Sidekick}
              disabled={!hasHealthyProviders(providersHealth)}
              onClick={async () => {
                const confirmed = await confirm({
                  title: "Shrink-wrap",
                  message:
                    "This will open the agent builder and launch Sidekick on this conversation so you can turn it into an agent.",
                  validateLabel: "Continue",
                });
                if (confirmed && conversation) {
                  const route = getAgentBuilderRoute(
                    owner.sId,
                    "new",
                    `conversationId=${conversation.sId}`
                  );
                  void router.push(route);
                }
              }}
            />
          )}
          {canLeave && (
            <DropdownMenuItem
              label="Leave"
              onClick={() => setShowLeaveDialog(true)}
              icon={XClose}
            />
          )}
          {canDelete && (
            <DropdownMenuItem
              label="Delete"
              onClick={() => setShowDeleteDialog(true)}
              icon={Trash01}
              variant="warning"
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
